import type { Ionicons } from '@expo/vector-icons';

import { INBOX_PLACES } from '@/utils/inbox-triage';

/**
 * The v2 sidebar's grouped navigation (#279).
 *
 * Data, not JSX, so the grouping is reviewable in one place and so a test can assert
 * "no destination has been dropped" — which is the real risk in a nav
 * refactor. Losing a maintenance user's "Unassigned" link is a silent regression that no
 * typecheck catches and nobody reports as a bug; they just stop using the feature.
 *
 * The mockup shows four groups. Items the mockup omits but the old sidebar had
 * (Inspections, Unassigned, Delegated, Tasks) are folded into the group they belong to
 * rather than dropped, for exactly that reason.
 */

export interface NavItem {
	/** Route name — navigates to `/${name}` unless `href` says otherwise. */
	name: string;
	/**
	 * Where a tap goes, when that is more than `/${name}` — a query string picking the tab a
	 * badge counts, say. Active-matching uses `name`, which is the PATHNAME, so a row whose
	 * `href` carries a query still highlights on the route it lands on.
	 */
	href?: string;
	/**
	 * Sub-paths this row must NOT claim, even though they sit underneath it.
	 *
	 * Longest-prefix already hands `/inbox/mine` to the "Assigned to me" row, because that row
	 * exists. The away-places (Archived, Spam, Waiting) are routes with no drawer row at all,
	 * and without this "All mail" would stay highlighted while you stand in Archived — the
	 * drawer disagreeing with the rail about where you are.
	 *
	 * Derived from {@link INBOX_PLACES} rather than written out, so adding a place cannot
	 * leave a stale highlight behind.
	 */
	excludePaths?: string[];
	/**
	 * Extra path prefixes this row ALSO stands for — routes with no row of their own that
	 * belong to it. Each competes in longest-prefix exactly like a `name`, so a more specific
	 * row beneath one of these prefixes still wins on its own path.
	 */
	alsoActiveOn?: string[];
	label: string;
	icon: keyof typeof Ionicons.glyphMap;
	/** Which permission/flag gates it — resolved by the sidebar, kept out of the data. */
	gate?: NavGate;
	/** Which live count to show, if any. */
	badge?: NavBadge;
	/**
	 * Substitute the label at render time.
	 *
	 * One item names a list whose MEANING depends on the viewer — "My Work Orders" for
	 * someone with a caseload, "All Open Work Orders" for an admin or accounting, whose
	 * "mine" is everything. The nav stays pure data; the drawer supplies the string.
	 */
	labelKey?: NavLabelKey;
	/**
	 * Fill the `[id]` segment of `name` at render time.
	 *
	 * For a row that links to ONE record the org has configured — its own vendor, say — whose
	 * id is data rather than a constant. `name` stays the route TEMPLATE, so the
	 * no-dead-links test still resolves it to a real file. A row whose id is unresolved is
	 * dropped rather than rendered as a link to nowhere (see {@link resolveNavItem}).
	 */
	idKey?: NavIdKey;
}

export type NavLabelKey = 'myWorkOrders' | 'selfVendor';

export type NavIdKey = 'selfVendor';

/** Resolved ids for `NavItem.idKey` rows. A key left out means "not configured". */
export type NavIds = Partial<Record<NavIdKey, string | null>>;

/** `item` with its `[id]` filled in, or `null` when the id it needs is not configured. */
export const resolveNavItem = (item: NavItem, ids: NavIds): NavItem | null => {
	if (!item.idKey) return item;
	const id = ids[item.idKey];
	if (!id) return null;
	const segment = encodeURIComponent(id);
	return {
		...item,
		name: item.name.replace('[id]', segment),
		href: item.href?.replace('[id]', segment),
	};
};

export interface NavGroup {
	key: string;
	label: string;
	icon: keyof typeof Ionicons.glyphMap;
	items: NavItem[];
	/** Collapsed by default. Work Orders is open, being where most people start. */
	defaultOpen?: boolean;
	gate?: NavGate;
}

/**
 * What the collapsed group heading shows: the sum of every child badge.
 *
 * Only sound while each badge counts actionable work. A badge on a browsing surface ("All
 * mail" once counted the whole working inbox) inflates the heading into a number nobody can
 * clear — the Inbox heading read 110 for a manager with three things waiting. Keep badges
 * off places; don't special-case the sum.
 *
 * Here rather than inline in the sidebar so it can be tested against the REAL groups.
 */
export function groupBadgeCount(
	group: NavGroup,
	badges: Partial<Record<NavBadge, number>>,
): number {
	return group.items.reduce((sum, i) => sum + (i.badge ? badges[i.badge] ?? 0 : 0), 0);
}

export type NavGate =
	| 'inspections'
	| 'maintenance'
	/** Maintenance OR contractor — the people who actually chase a blocked job. */
	| 'maintenanceStaff'
	| 'tasks'
	| 'inbox'
	| 'users'
	| 'materials'
	| 'reports'
	/**
	 * The accounting staff: the `accounting` flag AND the vendor write right — Admin,
	 * Accounting, or an `accounting_admin` holder (`useAccountingPermissions().canModify`).
	 *
	 * NOT `VIEW_VENDORS`: `ACCOUNTING_VIEWER_WITH_NOTES` grants it to every role, so it would
	 * put the rows in front of every property manager with the flag. `CREATE_VENDORS` is the
	 * permission the matrix actually withholds from them.
	 *
	 * A nav scope, not access control — the vendor pages stay reachable, read-only, from the
	 * Accounting page's menu for everyone with the flag, exactly as before.
	 */
	| 'vendors'
	/**
	 * Accounting administrators — the same people as `vendors` (the `accounting` flag AND
	 * `useAccountingPermissions().canModify`: Admin, Accounting, `accounting_admin`), under a
	 * name that does not suggest vendors. Gates the portfolio GL Codes page.
	 */
	| 'accountingAdmin'
	/**
	 * The Admin GROUP header — web only, and nothing more.
	 *
	 * Deliberately NOT "is this person an administrator". The sidebar already hides a group
	 * with no visible items (see {@link visibleNavGroups}), so the group's own gate must be
	 * the loosest condition under which the section could ever apply; who sees WHAT inside
	 * it is each item's own gate.
	 *
	 * It used to be `canAccessAdmin`, which is `role === Admin` and nothing else. That hid
	 * the whole section from a user holding a granular capability — a `user_admin` holder
	 * satisfied the Users row's own gate but could never reach it, because the parent was
	 * gone. A group gate must never be stricter than the union of its children.
	 */
	| 'adminSection'
	/**
	 * The super administrator: `role === Admin`, web only. Matches `api/index.ts`, where
	 * every `/admin/*` route except feature-flags requires exactly that role — these
	 * surfaces read or destroy ALL org data, so no capability flag stands in for them.
	 */
	| 'superAdmin'
	/**
	 * The operational admin tools: Activity History, Location Analytics, Documents Admin.
	 * Super admin, OR a core capability holder (`user_admin` / `association_admin` /
	 * `work_order_admin`) — see `hasAdminCapability`.
	 *
	 * Separate from {@link superAdmin} because these READ across the org but destroy
	 * nothing, whereas Backup, Restore and the SQL console can flatten it. Not folded into
	 * the rows' own narrower gates either: a user administrator needs the audit log to do
	 * the job, and that is not the same question as "may edit users".
	 *
	 * Each destination now enforces the SAME question — `hasAdminCapability` in
	 * `api/analytics.ts` and in `ADMIN_SUBS` (`api/documents/routes.ts`), and the matching
	 * screen guard under `app/admin/`. A nav gate that is merely looser than its route is how
	 * a row becomes a redirect back to the dashboard.
	 */
	| 'adminTools'
	/**
	 * An admin tool over ACCOUNTING data: ({@link adminTools} OR accounting write access) plus
	 * the `accounting` flag. The write half is there because the page's actions are accounting
	 * writes the Accounting role and `accounting_admin` hold on the server.
	 *
	 * Batches is the only one. Its endpoints all sit behind `ensureAccountingFlag`
	 * (`api/import-batches.ts`), which 404s the whole family, so a core capability holder who
	 * is not in accounting would get a row opening an empty page and a console full of 404s.
	 * A row whose destination refuses it is worse than no row — the same rule as
	 * {@link sqlAdmin}.
	 */
	| 'adminAccountingTools'
	| 'sqlAdmin'
	| 'settings'
	/** Accounting's review pass, and the Admin role standing in for it. */
	| 'reviewQueue'
	/** Everyone who approves — that is everyone but maintenance and contractors. */
	| 'approvalQueue'
	/** Accounting's billing pass: the review-queue holders who ALSO carry the accounting flag. */
	| 'billingQueue'
	/**
	 * Who may change how inbound mail is ROUTED — admins only.
	 *
	 * Its own gate rather than reusing `admin`: that one opens the Admin group (SQL console,
	 * feature flags, document admin), and this row has to be able to appear for a routing
	 * administrator without implying the rest of it.
	 */
	| 'inboxRules'
	| 'emailTemplates'
	/**
	 * The intake queues — Ownership Transfers and Needs Review (review requests):
	 * `canAccessIntakeQueues` — Admin role, Accounting role, or `accounting_admin`, each with
	 * the `accounting` flag. Narrower than `accounting`, which a property manager can hold.
	 */
	| 'intakeQueues';

export type NavBadge =
	| 'pending'
	| 'needsReview'
	| 'pendingApproval'
	| 'needsBilling'
	| 'followUps'
	| 'myWorkOrders'
	| 'overdue'
	| 'unassigned'
	| 'delegated'
	| 'assignedToMe'
	| 'needsTriage'
	| 'applications'
	| 'ownershipTransfers'
	/**
	 * The review-request queue. NOT `needsReview`, which is already taken by work orders —
	 * two badges sharing a key would each render the other's number somewhere.
	 */
	| 'reviewRequests';

/** Top-level, outside any group — the two destinations people reach for constantly. */
export const V2_TOP_ITEMS: NavItem[] = [
	{ name: 'index', label: 'Dashboard', icon: 'home' },
];

export const V2_GROUPS: NavGroup[] = [
	{
		key: 'work-orders',
		label: 'Work Orders',
		icon: 'construct',
		defaultOpen: true,
		items: [
			{ name: 'work-orders', label: 'Work Orders', icon: 'construct' },
			/**
			 * The viewer's caseload. "My Work Orders" means assigned-to-me for maintenance and
			 * created-by-me-or-in-my-associations for a manager; for an admin or accounting
			 * there is no "mine", so the same row reads "All Open Work Orders" and the server
			 * applies no narrowing. Route and filter are one — `?scope=mine` — so the label is
			 * the only thing that changes.
			 */
			{
				name: 'work-orders/mine',
				label: 'My Work Orders',
				labelKey: 'myWorkOrders',
				icon: 'person',
				badge: 'myWorkOrders',
			},
			/**
			 * Open work past its due date. Red badge: this is the one count in the group that
			 * means something has already gone wrong rather than something is waiting.
			 */
			{ name: 'work-orders/overdue', label: 'Overdue', icon: 'alarm', badge: 'overdue' },
			/**
			 * The review chain, as TWO rows (#496 → split).
			 *
			 * Needs Review is accounting's step — completed work with no customer-facing
			 * description yet. Needs Approval is the property manager's — reviewed work waiting
			 * on a signature, with a two-business-day clock running. They used to share one row
			 * whose identity flipped by role; an admin, who may do both jobs, could then only
			 * reach one of them. Each is gated to the people who act on it, so an admin sees
			 * both and a technician sees neither.
			 */
			{
				name: 'work-orders/needs-review',
				label: 'Needs Review',
				icon: 'create',
				gate: 'reviewQueue',
				badge: 'needsReview',
			},
			{
				name: 'work-orders/needs-approval',
				label: 'Needs Approval',
				icon: 'checkmark-circle',
				gate: 'approvalQueue',
				badge: 'pendingApproval',
			},
			/**
			 * The third and last stage of the same chain: approved work that has not become a
			 * bill. The page has existed since work-order billing shipped, but was only reachable
			 * from a Quick Action on the Accounting page, so the person whose queue it is never
			 * saw a count for it. Gated to the reviewers who also hold the accounting flag,
			 * because the API behind it 404s without the flag.
			 *
			 * `href` opens the READY TO BILL tab, which is what the badge counts. The page's own
			 * default is Needs Review, so without this the row said 16 and the screen showed 1.
			 */
			{
				name: 'accounting/work-order-billing',
				href: '/accounting/work-order-billing?tab=pending',
				label: 'Needs Billing',
				icon: 'cash',
				gate: 'billingQueue',
				badge: 'needsBilling',
			},
			/**
			 * Follow-up tasks live with the work orders they hang off, not under Accounting
			 * where the Tasks link used to sit: a follow-up is "go back to WO #31198", and the
			 * person chasing it thinks of it as work-order work. Badge is every OPEN follow-up,
			 * any assignee — the set the page lists. (The To Do panel's follow-ups are the
			 * viewer's own; a badge built from that read 1 over a page showing 2.)
			 */
			{
				name: 'tasks',
				// Only the follow-ups, as a list — not every task on a calendar.
				href: '/tasks?filter=follow-ups',
				label: 'Follow Up Tasks',
				icon: 'checkmark-circle',
				gate: 'tasks',
				badge: 'followUps',
			},
			{ name: 'quotes', label: 'Quotes', icon: 'receipt' },
			/**
			 * "Pending" is a PAUSE, not a queue: waiting on a part, a vendor, an owner.
			 * Only the people who chase those blockers need the page, so it is gated to
			 * maintenance staff rather than shown to every role as a to-do they cannot action.
			 */
			{ name: 'pending', label: 'Pending', icon: 'hourglass', gate: 'maintenanceStaff', badge: 'pending' },
			{ name: 'unassigned', label: 'Unassigned', icon: 'person-remove', gate: 'maintenance', badge: 'unassigned' },
			{ name: 'delegated', label: 'Delegated', icon: 'people-circle', gate: 'maintenance', badge: 'delegated' },
			{ name: 'inspections', label: 'Inspections', icon: 'camera', gate: 'inspections' },
			{ name: 'materials', label: 'Materials', icon: 'cube', gate: 'materials' },
			{ name: 'reports', label: 'Reports', icon: 'stats-chart', gate: 'reports' },
		],
	},
	{
		key: 'inbox',
		label: 'Inbox',
		icon: 'mail',
		// Collapsed, the heading is the SUM of every badge beneath it — everything in the group
		// that needs you. Rolling up only "Assigned to me" left the heading blank
		// over 10 unfiled messages and 15 exterior requests. Safe to sum now that "All mail"
		// carries no badge; the one overlap left is a message both assigned to you and wearing
		// a needs-human label, which counts twice until one of the two is cleared.
		items: [
			/*
				── THE ORDER IS THE RAIL'S ORDER ──

				All mail → Assigned to me → Needs Triage, matching `label-rail.tsx` top to
				bottom. These are the same six places rendered twice, and a drawer that
				ordered them by urgency while the rail ordered them by scope made the two
				read as different menus that happen to share words. Whichever order is
				right, both surfaces have to use it — someone who learns "mine is the second
				row" should not have to re-learn it one pane to the right.

				(The rail also shows Waiting / Spam / Archived between Assigned to me and
				Needs Triage. They have no drawer row — they are places you visit, not work
				that is waiting — so the drawer is a subsequence of the rail, not a
				reshuffle of it.)
			*/
			/**
			 * "All mail", not "Inbox" — the group is already called Inbox, so a child row of
			 * the same name said nothing. This is the everything-else view, named to match the
			 * rail's own top row so the two surfaces use one word for one thing.
			 *
			 * NO BADGE, deliberately. It counted the whole working inbox, which for a property
			 * manager includes every message already sitting on a colleague's desk — 30
			 * bill-pay sign-ups routed to the bookkeeper read as 30 things she owed. "All
			 * mail" is a place you go to look something up, not a queue, and the two rows
			 * below it are the ones that mean work. (It counted "Needs a Human" before that,
			 * which was right when the row was called Inbox and describes a different set
			 * again.)
			 */
			{
				name: 'inbox',
				label: 'All mail',
				icon: 'mail',
				gate: 'inbox',
				// Every other place is a sibling route under `/inbox`. The two with rows of their
				// own are handled by longest-prefix anyway; the away-places have no row, so
				// without this "All mail" would stay lit while you stand in Archived.
				excludePaths: INBOX_PLACES.filter((p) => p !== 'all').map((p) => `inbox/${p}`),
			},
			/**
			 * Mail handed to this person specifically, and still OPEN. The rail's own row is
			 * inside the inbox — you have to already be there to see it, which is precisely
			 * wrong for work someone is waiting on you for.
			 *
			 * BLUE, not the red it shipped with. Red is this drawer's "something has gone
			 * wrong" register — Overdue work, mail nobody owns — and a full personal queue is
			 * neither. It is just your work, and painting an ordinary Tuesday as an alarm is
			 * how people learn to stop reading the alarms.
			 *
			 * OPEN only. Work parked on a homeowner is the work you cannot act on, and
			 * counting it would make the badge unclearable and therefore ignorable.
			 */
			{
				// A ROUTE of its own (`app/inbox/mine.tsx`), so the pathname alone decides whether
				// it is active. It used to be `/inbox?assignedTo=me`, and a layout cannot read a
				// child's params reliably — see the header of `INBOX_PLACES` for why that put two
				// rows in the highlighted state at once.
				name: 'inbox/mine',
				label: 'Assigned to me',
				icon: 'person-circle',
				gate: 'inbox',
				badge: 'assignedToMe',
			},
			/**
			 * Mail nobody owns yet — last of the three, because it is the portfolio's problem
			 * rather than any one person's until somebody files it.
			 *
			 * A route of its own (`app/inbox/triage.tsx`) like the row above; the reserved
			 * `?label=needs-human` filter is what the screen applies, not what the nav matches.
			 *
			 * Shown to everyone with inbox access, not just support. The count is scoped
			 * server-side by `inboxScopeForUser`, so a property manager sees their own
			 * associations' unfiled mail and support sees the portfolio — the same number
			 * each of them sees in the rail, from the same query. Gating the row by role
			 * would be a second, hand-maintained answer to a question the server already
			 * answers correctly.
			 */
			{
				name: 'inbox/triage',
				label: 'Needs Triage',
				icon: 'alert-circle',
				gate: 'inbox',
				badge: 'needsTriage',
			},
			/**
			 * Lands on NEEDS REVIEW, not the full list — because that is what the badge counts.
			 *
			 * The count is `applicationCounts.pendingUs`, i.e. work waiting on this team. Sent
			 * to the index the row said 11 and opened "All Requests", where 11 is not a number
			 * on the screen anywhere; the reader has to go find their own queue. Same defect
			 * and same fix as the Needs Review row above.
			 *
			 * `name` stays the SECTION, so the row keeps its highlight while you drill into an
			 * individual request. Only the destination moves.
			 */
			{
				name: 'exterior-applications',
				href: '/exterior-applications/needs-review',
				label: 'Exterior Improvements',
				icon: 'home-outline',
				gate: 'inbox',
				badge: 'applications',
			},
			/**
			 * Resale packets from title companies, queued for a person to approve.
			 *
			 * Filed here rather than under Accounting because it is document intake — the same
			 * job as the Inbox and Exterior Improvements — and because v1 posts nothing: the
			 * transfer fee is recorded for the audit trail with no charge and no journal entry
			 * behind it. Its own surface, not an inbox place: a packet is not a message, and
			 * `INBOX_PLACES` feeds a list that could not render one.
			 *
			 * It SITS with the inbox but has its OWN gate, `intakeQueues`, resolved with the
			 * same `canAccessIntakeQueues` the API runs on every
			 * `/accounting/ownership-transfers` route: Admin role, Accounting role, or
			 * `accounting_admin`, each with the `accounting` flag. Property managers are out even
			 * with the accounting flag — the API 404s them. (Gated on `inbox` it once disagreed
			 * both ways; gated on bare `accounting` it showed every flagged PM the portfolio's
			 * buyer and seller names.)
			 */
			{
				name: 'ownership-transfers',
				label: 'Ownership Transfers',
				icon: 'swap-horizontal',
				gate: 'intakeQueues',
				badge: 'ownershipTransfers',
			},
			/**
			 * Work an automation stopped and handed to a person.
			 *
			 * Sits below Ownership Transfers because it is the same job one step more general:
			 * that queue holds packets an importer could not decide about, this one holds
			 * PROPOSED ACTIONS with no domain table of their own — which is exactly what an
			 * agent produces when it meets something it was not taught.
			 *
			 * Gated on `intakeQueues`, like its neighbour, because every
			 * `/accounting/review-requests` route runs `ensureAccountingFlag` and then the same
			 * `canAccessIntakeQueues`: the agent's escalations carry transfer-packet detail, so a
			 * property manager holding only the accounting flag is 404ed and gets no row. (Gated
			 * on `inbox` it would have shown the row to people whose every request 404s.)
			 */
			{
				name: 'review-requests',
				label: 'Needs Review',
				icon: 'help-circle-outline',
				gate: 'intakeQueues',
				badge: 'reviewRequests',
			},
			{ name: 'documents', label: 'Documents', icon: 'folder-open' },
			/**
			 * Inbox CONFIGURATION, filed with the inbox rather than under Settings.
			 *
			 * It lived under Settings first and was invisible in practice: the people who
			 * decide "bill pay sign-ups go to Bryce" are working the inbox when they decide
			 * it, and nobody goes looking for inbox behaviour in a general settings page.
			 * Settings keeps its entry too — deleting it would break anyone's bookmark.
			 */
			{
				name: 'settings/inbox-rules',
				label: 'Routing Rules',
				icon: 'git-branch-outline',
				gate: 'inboxRules',
			},
			/**
			 * The wording the inbox sends, filed next to the rules that route it.
			 *
			 * Same argument as Routing Rules above: whoever decides how a reply should read is
			 * working the inbox when they decide it, and nobody goes hunting for outbound prose
			 * in a general settings page. Settings keeps its own entry, so existing bookmarks
			 * and muscle memory still work.
			 *
			 * Gated on being able to EDIT, not merely to view. The page renders read-only for
			 * everyone else, and a nav row promising a settings screen that turns out to be a
			 * museum is worse than no row.
			 */
			{
				name: 'settings/email-templates',
				label: 'Email Templates',
				icon: 'mail-open-outline',
				gate: 'emailTemplates',
			},
		],
	},
	{
		key: 'accounting',
		label: 'Accounting',
		icon: 'calculator',
		items: [
			// The ONE association row: `/associations` carries the accounting figures and menu
			// for flagged viewers. The old "Accounting" picker row is gone — `/accounting` is a
			// redirect kept for bookmarks. The portfolio pages its menu opens (`/accounting/portfolio`,
			// GL Codes, Tags, …) and the per-association reports have no row, so this one claims
			// `/accounting/*`; Vendors and Needs Billing are longer and keep their own paths.
			{ name: 'associations', label: 'Associations', icon: 'business', alsoActiveOn: ['accounting'] },
			{ name: 'accounting/vendors', label: 'Vendors', icon: 'briefcase-outline', gate: 'vendors' },
			/**
			 * The management company's OWN vendor record — the one work-order bills are issued
			 * under, where the invoices sent to associations are printed from (#578).
			 *
			 * Identified by `organizations.self_vendor_id`, the same pointer work-order billing
			 * reads (`resolveSelfVendor`); never by name. Labelled with the organization's name,
			 * and absent until Settings → Organization picks a self vendor.
			 */
			{
				name: 'accounting/vendors/[id]',
				label: 'Our Vendor Record',
				labelKey: 'selfVendor',
				icon: 'storefront-outline',
				gate: 'vendors',
				idKey: 'selfVendor',
			},
			// The chart of accounts. Was a button on each association's Accounting tab; the page
			// was always portfolio-wide. Its own row, so it out-ranks Associations' `/accounting`.
			{ name: 'accounting/gl-codes', label: 'GL Codes', icon: 'list-outline', gate: 'accountingAdmin' },
		],
	},
	{
		key: 'admin',
		label: 'Admin',
		icon: 'settings',
		gate: 'adminSection',
		// Every row carries its own gate ON PURPOSE. When the group gate was the thing
		// deciding who got in, an ungated row meant "super admin" only by inheritance — so
		// loosening the group would silently have exposed Backup and Restore. Spelling each
		// one out makes the group gate safe to loosen and keeps this list honest about what
		// the server actually enforces.
		items: [
			{ name: 'users', label: 'Users', icon: 'people', gate: 'users' },
			{ name: 'admin/location-analytics', label: 'Location Analytics', icon: 'locate', gate: 'adminTools' },
			// Backup and Restore read or overwrite EVERY table. They stay with the role.
			{ name: 'admin/backup', label: 'Backup', icon: 'cloud-upload', gate: 'superAdmin' },
			{ name: 'admin/restore', label: 'Restore', icon: 'cloud-download', gate: 'superAdmin' },
			{ name: 'admin/history', label: 'Activity History', icon: 'time', gate: 'adminTools' },
			{ name: 'admin/import-batches', label: 'Batches', icon: 'archive-outline', gate: 'adminAccountingTools' },
			{ name: 'admin/documents', label: 'Documents Admin', icon: 'folder-open-outline', gate: 'adminTools' },
			{ name: 'admin/sql', label: 'SQL Admin', icon: 'code-slash', gate: 'sqlAdmin' },
		],
	},
];

/**
 * The groups a user actually sees, with only the rows they may open.
 *
 * Exported and pure so the gate wiring is testable without mounting the sidebar — the
 * rule below is the whole reason a `user_admin` holder lost the Users row, and that was
 * invisible to every existing test because it lived inside a `useMemo`.
 *
 * A group survives only if it has at least one visible row, which is what lets a group's
 * own gate be the loose "could this section ever apply" question rather than a second,
 * stricter permission check that can silently outrank its children.
 *
 * `ids` fills in `idKey` rows; one whose id is unresolved is dropped like a gated-off row.
 */
export const visibleNavGroups = (gates: Record<NavGate, boolean>, ids: NavIds = {}): NavGroup[] => {
	const visible = (item: NavItem | NavGroup): boolean => !item.gate || gates[item.gate];
	return V2_GROUPS
		.filter(visible)
		.map((g) => ({ ...g, items: resolveNavItems(g.items.filter(visible), ids) }))
		.filter((g) => g.items.length > 0);
};

const resolveNavItems = (items: NavItem[], ids: NavIds): NavItem[] =>
	items.flatMap((i) => resolveNavItem(i, ids) ?? []);

/** Pinned to the bottom, as in the mockup. */
export const V2_BOTTOM_ITEMS: NavItem[] = [
	{ name: 'settings', label: 'Settings', icon: 'settings', gate: 'settings' },
];

/**
 * The WHOLE menu on the iPhone app (#684), for every role: the work-order list (which is Home
 * there) and Settings, where sign-out lives. The grouped nav opened desktop screens — tables,
 * column browsers — that a phone cannot use and that left people stranded without a header.
 *
 * Settings is ungated on purpose: it is the only way to sign out. Work Orders also stands for
 * `/work-orders/*`, so it stays lit on a job opened from the list.
 */
export const PHONE_NAV_ITEMS: NavItem[] = [
	{ name: 'index', label: 'Work Orders', icon: 'construct', alsoActiveOn: ['work-orders'] },
	{ name: 'settings', label: 'Settings', icon: 'settings' },
];

/** Every route the v2 sidebar can reach — used by the no-regression test. */
/**
 * Every nav row, flat — the sidebar needs the items themselves for active-matching.
 *
 * Without `ids`, `idKey` rows keep their route TEMPLATE (what the dead-link test checks).
 * With them, those rows are resolved — or dropped — so longest-prefix sees the real path:
 * otherwise "Vendors" would stay lit on the org's own vendor page beside its own row.
 */
export const allV2Items = (ids?: NavIds): NavItem[] => {
	const items = [...V2_TOP_ITEMS, ...V2_GROUPS.flatMap((g) => g.items), ...V2_BOTTOM_ITEMS];
	return ids ? resolveNavItems(items, ids) : items;
};

export const allV2RouteNames = (): string[] => allV2Items().map((i) => i.name);

/**
 * Which sidebar row is the active one — AT MOST ONE, by construction.
 *
 * Pure and exported so the rule can be unit-tested directly. It was inline in the sidebar
 * and grew three interacting cases (longest-prefix, filtered views, away-places) that a
 * render test can only reach awkwardly.
 *
 * ── THE RULE, IN ORDER ──
 *
 * 1. The pathname has to match at all.
 * 2. A row loses to any `excludePaths` entry the pathname is under.
 * 3. Otherwise longest-prefix wins, so `/work-orders/overdue` does not also light
 *    `/work-orders`, and `/inbox/mine` does not also light "All mail". A row's
 *    `alsoActiveOn` prefixes compete alongside every `name`.
 *
 * ── WHY IT NO LONGER READS PARAMS ──
 *
 * It used to, and that was the bug: rows like "Assigned to me" were `/inbox?assignedTo=me`,
 * so the sidebar had to match on query parameters it could only get from
 * `useGlobalSearchParams` — which MERGES across the navigation state. A stale `?label=`
 * survived a move to `?assignedTo=`, both rows matched, and both highlighted. Three attempts
 * at making the matching rule cleverer (specificity across siblings, exclusion lists,
 * first-match-wins) each moved the failure somewhere else, because the input was wrong rather
 * than the rule.
 *
 * Every place is now a route (see `INBOX_PLACES`). A pathname has exactly one value, so two
 * rows can no longer both be current — by construction, not by arbitration. `params` stays in
 * the signature only so callers need not care; nothing reads it.
 */
export function isNavItemActive(
	item: NavItem,
	ctx: { pathname: string; params?: Record<string, unknown>; items?: NavItem[]; routeNames?: string[] },
): boolean {
	const { pathname } = ctx;
	const items = ctx.items ?? allV2Items();
	const routeNames = ctx.routeNames ?? items.map((i) => i.name);
	const { name } = item;

	/** At a SEGMENT boundary, so `/inbox-rules` is not "inside" `/inbox`. */
	const isUnder = (path: string): boolean => pathname === `/${path}` || pathname.startsWith(`/${path}/`);

	// `index` is a prefix of nothing, so it matches `/` exactly — plus any `alsoActiveOn` (the
	// phone's Work Orders row, which is Home there).
	if (name === 'index') return pathname === '/' || pathname === '/index' || !!item.alsoActiveOn?.some(isUnder);

	const mine = [name, ...(item.alsoActiveOn ?? [])].filter(isUnder);
	if (mine.length === 0) return false;
	if (item.excludePaths?.some(isUnder)) return false;

	const best = Math.max(...mine.map((p) => p.length));
	const others = [
		...routeNames.filter((other) => other !== name),
		...items.filter((other) => other.name !== name).flatMap((other) => other.alsoActiveOn ?? []),
	];
	return !others.some((other) => other.length > best && isUnder(other));
}
